import { GLContext } from 'nanogl/types'
import Node from 'nanogl-gltf/lib/elements/Node'
import Gltf from 'nanogl-gltf/lib/Gltf'
import { GltfLoaderOptions } from 'nanogl-gltf/lib/io/GltfLoaderOptions'
import Bounds from 'nanogl-pbr/Bounds'

import { RenderContext } from '@/webgl/core/RenderContext'
import GltfResource from '@/webgl/resources/GltfResource'
import ILoadable from '@/webgl/runner/interfaces/ILoadable'
import { IRenderable } from '@/webgl/runner/interfaces/IRenderable'

export type AnimatedSpriteSettings = {
  framerate?: number
  loop?: boolean
  autoplay?: boolean
}

export default class AnimatedSprite implements IRenderable, ILoadable {
  gltf: Gltf

  id: string
  node: Node = new Node()
  framerate: number = 60

  loop: boolean = false
  autoplay: boolean = false
  playing: boolean = false

  get currentFrame() {
    return this._currentFrame
  }
  get frames() {
    return this._frames
  }
  get duration() {
    return this._duration
  }

  get currentTime() {
    return this._currentTime
  }
  set currentTime(v: number) {
    this._currentTime = v
    this.updateCurrentFrame()
  }

  private _frames: number = 0
  private _currentFrame: number = 0
  private _currentTime: number = 0
  private _duration: number = 0

  constructor(
    private readonly request: string,
    private readonly gl: GLContext,
    private opts?: GltfLoaderOptions
  ) {}

  setOptions(opts: GltfLoaderOptions) {
    this.opts = opts
  }

  async load(abort: AbortSignal): Promise<AnimatedSprite> {
    this.gltf = await new GltfResource(this.request, this.gl, this.opts).load()

    this._frames = this.gltf.renderables.length
    this._duration = this._frames / this.framerate
    this.currentTime = 0

    this.gltf.renderables.sort((a, b) => a.node.name.localeCompare(b.node.name))
    this.node.add(this.gltf.root)
    return this
  }

  private updateCurrentFrame() {
    const frame = Math.floor((this._currentTime / this._duration) * this._frames)
    if (this.loop == false) this._currentFrame = Math.min(this._frames - 1, frame)
    else this._currentFrame = frame % this._frames
  }

  getBounds(): Bounds {
    this.gltf.renderables[this._currentFrame].computeBounds()
    return this.gltf.renderables[this._currentFrame].bounds
  }

  preRender(dt: number) {
    if (!this.playing) return
    this._currentTime += dt
    if (this._currentTime > this._duration) {
      if (this.loop) this._currentTime -= this._duration
      else this._currentTime = this._duration
    }
    this.updateCurrentFrame()
  }

  public render(context: RenderContext) {
    this.gltf.renderables[this._currentFrame].node._wmatrix.set(this.node._wmatrix)
    this.gltf.renderables[this._currentFrame].render(
      context.gl,
      context.camera,
      context.mask,
      context.pass,
      context.glConfig
    )
  }
}
